import { Apollo, gql } from 'apollo-angular';
import { Injectable } from '@angular/core';
import { BaseRepository } from 'src/app/services/remote-data-service/repositories/base.repository';
import { Project } from 'src/app/entities/project/project';
import { ProjectDto } from 'src/app/services/remote-data-service/dtos/project.dto';
import { ProjectMapper } from 'src/app/services/remote-data-service/mappers/project.mapper';
import { catchError, map } from 'rxjs';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import log from 'loglevel';


/**
 * GraphQL arguments for get_projects query.
 */
export interface ProjectsFilters {
  page?: number;
  limit?: number;
  clients?: string[]
  title?: string
  members?: string[]
  groups?: string[]
}


/**
 * GraphQL query used by this gateway.
 *
 * Exported because is required by tests.
 */
@Injectable({
  providedIn: 'root',
})
export class ProjectRepository extends BaseRepository<Project, ProjectDto> {
  protected override _mapper = new ProjectMapper();
  protected override dataPropertyName = 'get_projects';

  protected override logger = log.getLogger('ProjectRepository');

  constructor(
    protected override apollo: Apollo,
    protected override snackBar: SnackbarService
  ) {
    super(apollo, snackBar);
  }

  /******************/
  /* Public methods */
  /******************/
  public override getGqlQuery() {
    return gql`
      query ProjectsQuery($page: Int, $limit: Int, $clients: [ID], $title: String, $members: [ID!], $groups: [ID!]) {
        get_projects (page: $page, limit: $limit, clients: $clients, title: $title, members: $members, groups: $groups) {
          items {
            uuid
            title
            color
            archived
            active
            created
            time_spent
            budget
            members {
              uuid
              name
            }
            client {
              uuid
              name
            }
            groups {
              name
              uuid
            }
            permissions {
              update
              delete
              track
            }
          }
          total
          pager {
            current
            next
            prev
          }
        }
      }
    `;
  }


  /**
   * Function to add new projects
   * @param project
   * @returns A boolean observable that indicated if query was successful.
   */
  public addNewProject(project: Project) {
    const ADDPROJECT = gql`
      mutation MyMutation($uuid: String, $members: [ID!], $archived: Boolean, $active: Boolean, $budget: Int, $color: String!, $title: String!, $client: String!) {
        create_project(
          data: {
              uuid: $uuid,
              members: $members, 
              archived: $archived,
              active: $active,
              budget: $budget, 
              client: $client, 
              color: $color, 
              title: $title,
              private: false
          }
        ) {
          project {
            title
            uuid
            members {
              uuid
            }
          }
          errors
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation: ADDPROJECT,
        variables: {
          uuid: project.id,
          archived: project.archived,
          active: project.active,
          budget: project.budget,
          color: project.color,
          title: project.name,
          client: project.client.id,
          members: project.members?.map(project => project.id)
        },
      })
      .pipe(
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error)
          return [];
        })
      );
  }


  public updateProject(project: Project) {
    const UPDATEPROJECT = gql`
      mutation MyMutation($projectId: ID!, $archived: Boolean, $active: Boolean, $budget: Int, $color: String, $title: String, $client: String) {
        update_project(
          uuid: $projectId,
          data: {
            archived: $archived, 
            budget: $budget,
            color: $color, 
            title: $title,
            client: $client,
            active: $active
          }
        ) {
          project {
            title
            uuid
            members {
              uuid
            }
          }
          errors
        }
      }
    `

    return this.apollo
      .mutate({
        mutation: UPDATEPROJECT,
        variables: {
          projectId: project.id,
          archived: project.archived,
          budget: project.budget,
          color: project.color,
          title: project.name,
          client: project.client.id,
          active: project.active
        }
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error)
          return [];
        })
      )
  }


  public removeProject(project: Project) {
    const REMOVEPROJECT = gql`
      mutation MyMutation {
        delete_project(uuid: "${project.id}") {
          project {
            title
            uuid
            members {
              uuid
            }
          }
          errors
        }
      }
    `

    return this.apollo
      .mutate({
        mutation: REMOVEPROJECT,
        variables: {}
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error)
          return [];
        })
      )
  }
}
